import { failure, type Result, success } from '@orus.eu/result'

import {
  AbstractDimension,
  type DimensionOptions,
  type DimensionValidationProblem,
  dimensionValidationFailure,
  type LooselyTypedValue,
} from './abstract-dimension.ts'

export class StringsArrayDimension<NAME extends string = string, TYPE = string> extends AbstractDimension<
  NAME,
  readonly TYPE[]
> {
  readonly allowedValues: readonly TYPE[]
  readonly labels: { [key in TYPE as string]: string }
  /** A value that cannot be combined with the others, typically a "none of these" choice. Offered to the UI as a picking rule, not enforced by `validateData`. */
  readonly exclusiveValue: TYPE | undefined
  constructor(
    options: DimensionOptions<NAME, readonly TYPE[]> & {
      allowedValues: readonly TYPE[]
      labels: { [key in TYPE as string]: string }
      exclusiveValue?: TYPE | undefined
    },
  ) {
    super(options)
    this.allowedValues = options.allowedValues
    this.labels = options.labels
    this.exclusiveValue = options.exclusiveValue

    this.mcpAdapter = {
      parseValueFromMcp(mcpValueInput: string): Result<readonly TYPE[], string> {
        const pipeSeparatedValues = mcpValueInput.split('|')
        const everyValueIsAllowed = pipeSeparatedValues.every((val) => options.allowedValues.includes(val as TYPE))
        if (!everyValueIsAllowed) {
          return failure(`Invalid input format: ${mcpValueInput}. ${formattingInstructions}`)
        }
        return success(pipeSeparatedValues as unknown as readonly TYPE[])
      },
      formattingInstructions,
    }
  }

  override validateData(_value: LooselyTypedValue): Result<readonly TYPE[], DimensionValidationProblem> {
    if (!Array.isArray(_value)) {
      return dimensionValidationFailure(`Field ${this.name} is not an array`)
    }
    if (!_value.every((val) => this.allowedValues.includes(val))) {
      throw new Error(`Invalid value: ${_value.join(', ')}`)
    }
    return success(_value)
  }

  override formatToString(_value: TYPE[]): string | undefined {
    return _value.map((val) => this.labels[val as string]).join('\n')
  }
}

const formattingInstructions = 'The values, separated by the pipe character "|".'
